import React from "react";
import { cx } from "../../utils/classNames";
import { typographyRole } from "../../utils/typography";

const variantSizeMap = {
  heading: {
    sm: typographyRole.headingSm,
    md: typographyRole.headingMd,
    lg: typographyRole.headingLg,
    xl: typographyRole.headingXl,
  },
  display: {
    sm: typographyRole.displaySm,
    md: typographyRole.displayMd,
    lg: typographyRole.displayLg,
    xl: typographyRole.displayXl,
  },
} as const;

const heightVariants = {
  page: "h-16 leading-[62px]",
} as const;

type HeadingVariant = keyof typeof variantSizeMap;
type HeadingSize = keyof (typeof variantSizeMap)["heading"] | "xs";
type HeadingHeight = keyof typeof heightVariants;

/**
 * Heading
 *
 * Typography component for page/section headings.
 *
 * - `variant="heading"` (default) uses the Inter heading scale — for page titles,
 *   section titles, card titles, and other product chrome.
 * - `variant="display"` uses the display scale — reserved for brand moments
 *   (signup, marketing surfaces) and big-number KPIs.
 *
 * Both variants share the same `sm` / `md` / `lg` / `xl` size scale.
 */
export interface HeadingProps {
  tag?: React.ElementType;
  /** Type scale to render in. Defaults to "heading". */
  variant?: HeadingVariant;
  /** Size within the chosen variant's scale. */
  size?: HeadingSize;
  height?: HeadingHeight;
  className?: string;
  children?: React.ReactNode;
}

export function Heading({
  tag: Tag = "h1",
  variant = "heading",
  size = "lg",
  height,
  className = "",
  children,
}: HeadingProps) {
  const resolvedSize = size === "xs" ? "sm" : size;
  const sizeClasses = variantSizeMap[variant][resolvedSize];
  const heightClasses = height ? heightVariants[height] : "";

  return <Tag className={cx(sizeClasses, "text-text-primary", heightClasses, className)}>{children}</Tag>;
}
